import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  createTopLayerHost,
  hideTopLayerHost,
  raiseTopLayerHost,
} from '@ardoq/dom-utils';

/**
 * Tag name of the element hosting the tour UI, and so also the selector that
 * matches it. A page whose own keyboard or pointer handling has to tell the
 * tour apart from the page needs this: the host lives on `document.body`,
 * outside whatever subtree the page reasons about.
 */
export const TOUR_HOST_TAG_NAME = 'tour-container';

/**
 * A tour must paint above everything it can be pointed at, modals included: the
 * homepage tour opens the search modal and then spotlights inside it. Modals sit
 * in the browser's top layer, which no `z-index` can outrank, so the tour is
 * hosted there too.
 *
 * The UA popover box is neutralised so the `position: fixed; inset: 0` children
 * inside keep positioning against the viewport. `pointer-events: none` keeps the
 * host itself out of hit testing — it spans the viewport, and steps with
 * `allowInteraction` need clicks to reach the page. Children that must be
 * clickable set `pointer-events: auto` themselves.
 */
const createHost = (): HTMLElement => createTopLayerHost(TOUR_HOST_TAG_NAME);

type TopLayerPortalProps = {
  children: ReactNode;
  /**
   * Re-asserts the host on top of the layer whenever this changes. Top-layer
   * paint order is the order elements were shown, so a step that opens a modal
   * as part of `beforeShow` would otherwise leave the tour behind that modal.
   */
  reassertKey: string;
};

export const TopLayerPortal = ({
  children,
  reassertKey,
}: TopLayerPortalProps) => {
  const hostRef = useRef<HTMLElement | null>(null);
  if (!hostRef.current) {
    hostRef.current = createHost();
  }
  const host = hostRef.current;

  useLayoutEffect(() => {
    document.body.appendChild(host);
    return () => {
      hideTopLayerHost(host);
      host.remove();
    };
  }, [host]);

  useLayoutEffect(() => {
    raiseTopLayerHost(host);
  }, [host, reassertKey]);

  return createPortal(children, host);
};
